import React from "react";
import { Link } from "react-router-dom";
import { useStore } from "@/contexts/StoreContext";
import { Trash2, Minus, Plus, ArrowRight } from "lucide-react";

const Cart = () => {
    const { cart, updateQty, removeFromCart, cartTotal, cartCount } = useStore();

    return (
        <div data-testid="cart-page">
            <section className="px-4 md:px-8 py-10 md:py-16 border-b-4 border-ink">
                <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-hazard">
                    [Bag / Step 01 of 02]
                </span>
                <h1 className="font-heading text-6xl md:text-8xl uppercase leading-[0.85] mt-2">
                    Your Bag
                </h1>
                <p className="mt-2 font-mono-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {cartCount} pieces locked in
                </p>
            </section>

            {cart.length === 0 ? (
                <section className="px-4 md:px-8 py-24 text-center border-b-4 border-ink">
                    <p className="font-heading text-5xl uppercase">Bag is empty.</p>
                    <p className="mt-3 font-mono-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        Go fill it with weapons.
                    </p>
                    <Link
                        to="/shop"
                        className="inline-block mt-8 bg-ink text-black border-2 border-ink px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-hazard hover:border-hazard"
                        data-testid="cart-empty-shop-button"
                    >
                        Shop The Drop
                    </Link>
                </section>
            ) : (
                <section className="grid grid-cols-1 lg:grid-cols-12 border-b-4 border-ink">
                    <div className="lg:col-span-8 lg:border-r-4 border-ink">
                        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b-2 border-ink font-mono-ui text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                            <div className="col-span-6">Product</div>
                            <div className="col-span-2 text-center">Price</div>
                            <div className="col-span-2 text-center">Qty</div>
                            <div className="col-span-2 text-right">Total</div>
                        </div>
                        {cart.map((item) => (
                            <div
                                key={item.key}
                                className="grid grid-cols-12 gap-4 px-4 md:px-6 py-4 border-b-2 border-dotted border-ink/40 items-center"
                                data-testid={`cart-row-${item.product_id}`}
                            >
                                <div className="col-span-12 md:col-span-6 flex gap-4 items-center">
                                    <div className="w-20 h-24 bg-bone border-2 border-ink flex-shrink-0 overflow-hidden">
                                        {item.image && (
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-heading text-xl uppercase leading-none">
                                            {item.name}
                                        </h3>
                                        <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            Size {item.size} / {item.color}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => removeFromCart(item.key)}
                                            className="mt-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-hazard hover:underline inline-flex items-center gap-1"
                                            data-testid={`cart-page-remove-${item.product_id}`}
                                        >
                                            <Trash2 className="w-3 h-3" /> Remove
                                        </button>
                                    </div>
                                </div>
                                <div className="col-span-4 md:col-span-2 md:text-center font-mono-ui text-sm">
                                    ${item.price.toFixed(2)}
                                </div>
                                <div className="col-span-4 md:col-span-2 flex md:justify-center">
                                    <div className="inline-flex items-center border-2 border-ink">
                                        <button
                                            type="button"
                                            onClick={() => updateQty(item.key, item.quantity - 1)}
                                            className="px-2 py-1 hover:bg-ink hover:text-black"
                                            data-testid={`cart-page-decrease-${item.product_id}`}
                                            aria-label="Decrease"
                                        >
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <span className="px-3 font-mono-ui text-sm">
                                            {item.quantity}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => updateQty(item.key, item.quantity + 1)}
                                            className="px-2 py-1 hover:bg-ink hover:text-black"
                                            data-testid={`cart-page-increase-${item.product_id}`}
                                            aria-label="Increase"
                                        >
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                                <div className="col-span-4 md:col-span-2 md:text-right font-mono-ui text-sm">
                                    ${(item.price * item.quantity).toFixed(2)}
                                </div>
                            </div>
                        ))}
                    </div>

                    <aside className="lg:col-span-4 p-6 md:p-8 bg-bone">
                        <h2 className="font-heading text-3xl uppercase">Summary</h2>
                        <div className="mt-6 space-y-3 font-mono-ui text-xs uppercase tracking-[0.15em]">
                            <Row label="Subtotal" value={`$${cartTotal.toFixed(2)}`} />
                            <Row label="Billing" value="Collected on form" />
                            <Row label="Delivery" value="Confirmed manually" />
                        </div>
                        <div className="mt-6 pt-6 border-t-2 border-ink flex justify-between items-baseline">
                            <span className="font-mono-ui text-xs uppercase tracking-[0.2em]">
                                Estimated Total
                            </span>
                            <span className="font-heading text-3xl">
                                ${cartTotal.toFixed(2)}
                            </span>
                        </div>
                        <Link
                            to="/checkout"
                            className="mt-6 flex items-center justify-center gap-2 w-full bg-ink text-black border-2 border-ink py-4 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-hazard hover:border-hazard"
                            data-testid="cart-page-checkout-button"
                        >
                            Continue To Custom Checkout <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/shop"
                            className="block text-center mt-3 font-mono-ui text-[10px] uppercase tracking-[0.25em] hover:text-hazard"
                        >
                            Continue Browsing
                        </Link>
                    </aside>
                </section>
            )}
        </div>
    );
};

const Row = ({ label, value }) => (
    <div className="flex justify-between">
        <span>{label}</span>
        <span>{value}</span>
    </div>
);

export default Cart;
